import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const KIND_LABEL = {
  message: "Conversation", meeting_request: "Inquiry", connection: "Connection",
  event: "Event", otter_assist: "Otter Assist", contact_exchange: "Contact card",
  user_logged: "Logged",
};

/**
 * Relationship timeline. Rows are references + minimal metadata — never message bodies,
 * post content or note text. Anything the owner can no longer access is simply absent.
 */
export default function ContactTimeline({ contactId }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: body } = await api.get(`/crm/contacts/${contactId}/timeline`);
      setData(body);
    } catch { setData({ events: [], hidden_unavailable: 0 }); }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim()) return;
    try {
      await api.post(`/crm/contacts/${contactId}/timeline`, { title });
      setTitle(""); setAdding(false); load();
    } catch { toast.error("Couldn't add that entry"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/crm/contacts/${contactId}/timeline/${id}`);
      load();
    } catch { toast.error("Couldn't remove that entry"); }
  };

  return (
    <section data-testid="contact-timeline">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-[640]">Timeline</h2>
        <button type="button" onClick={() => setAdding((v) => !v)} data-testid="timeline-add"
          className="inline-flex items-center gap-1 text-sm font-[560] text-primary hover:underline">
          <Plus className="h-4 w-4" /> Log something
        </button>
      </div>

      {adding && (
        <div className="mt-3 flex gap-2" data-testid="timeline-form">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={150}
            aria-label="What happened?" data-testid="timeline-input"
            placeholder="Coffee at the summit, intro to Dev…" autoFocus />
          <Button size="sm" onClick={add} disabled={!title.trim()} data-testid="timeline-save">
            Add
          </Button>
        </div>
      )}

      <ol className="mt-4 space-y-2">
        {!data && <li className="text-sm text-muted-foreground">Loading…</li>}
        {data?.events?.length === 0 && (
          <li className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground"
            data-testid="timeline-empty">
            Nothing yet. Startup Otter activity with this person shows up here automatically.
          </li>
        )}
        {data?.events?.map((e) => (
          <li key={e.id} data-testid={`timeline-event-${e.id}`}
            className="flex items-start gap-3 rounded-lg border border-border bg-card p-3">
            <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-[560]">{e.title || KIND_LABEL[e.kind] || "Activity"}</p>
              <p className="text-xs text-muted-foreground">
                {KIND_LABEL[e.kind] || e.kind}
                {e.occurred_at && ` · ${new Date(e.occurred_at).toLocaleDateString()}`}
                {e.meta?.direction && ` · ${e.meta.direction}`}
              </p>
            </div>
            {e.href && (
              <Link to={e.href} className="shrink-0 text-xs font-[560] text-primary hover:underline"
                data-testid={`timeline-open-${e.id}`}>Open</Link>
            )}
            {e.source === "user" && (
              <button type="button" onClick={() => remove(e.id)} aria-label="Remove entry"
                data-testid={`timeline-delete-${e.id}`}
                className="shrink-0 rounded p-1 text-muted-foreground hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </li>
        ))}
      </ol>
      {data?.hidden_unavailable > 0 && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="timeline-hidden">
          {data.hidden_unavailable} item(s) are no longer available to you and are hidden.
        </p>
      )}
    </section>
  );
}
